// Every word the llama page shows. Stand-ins until the real copy is written.
// The player who hosts the room is "The Countant", never "the streamer". Every
// word stays in llama and BORC lore: no stream, viewer or chat.

export const STRINGS = {
  title: 'Llamallink',
  codeLabel: 'Room code',
  nameLabel: 'Your name',
  join: 'Join',
  badCode: 'That code is four letters.',
  connecting: 'Connecting',
  reconnecting: 'Reconnecting',
  queue: (place: number) => `You are #${place} in line`,
  queueNoPlace: 'Waiting for a seat',
  booted: (stage: number) => `You were removed on stage ${stage} for missing a deadline.`,
  waiting: 'Waiting for the next stage',
  llinking: 'Llinking...',
  llinked: "You're llinked.",
  guessAsk: 'How many sheep?',
  countAsk: (stage: number) => `Stage ${stage}`,
  countHint: 'Click the clicker',
  closing: (seconds: number) => `${seconds}s until deadline`,
  lockIn: 'Lock in',
  bullseye: 'You had it right.',
  runOver: 'Run over.',
  runQuit: 'The Countant walked off the job.',
  runStages: 'Stages',
  runRight: 'Right',
  runTips: 'Tips',
  runNext: 'Waiting for the next run',
  runStarting: 'A new run is starting.',
  paused: "Game's paused.",
  hostAway: "The Countant's gone.",
  leave: 'Leave',
  // A stand-in for Rob: the leave key's word once a first press arms it.
  leaveSure: 'Sure?',
  // Stand-ins for Rob: the timecard slip of a result and of a run's end.
  slip: {
    stage: (stage: number) => `Stage ${stage}`,
    // The status line already says the run is over, so the slip names itself.
    run: 'Timecard',
    offBy: 'Off by',
    tip: 'Tip',
    demerit: 'Demerit',
    bullseye: 'bullseye',
    docked: 'docked',
    paid: 'paid',
  },
  roomLink: 'Room link',
  copyLink: 'Copy link',
  linkCopied: 'Copied!',
  soundOn: 'Sound on',
  soundOff: 'Sound off',
  ended: {
    kicked: 'You were removed.',
    full: "Room's full.",
    gone: "Room's closed.",
    old: 'New version! Reload page!!!',
    closed: 'Llama Link is closed for now. Try again later.',
    room_locked: 'This room is not taking new llamas right now.',
    cleared: 'The Countant cleared the line. Join again to get back in it.',
  },
  challenge: 'One moment. Checking that you are a person.',

  // The party's vote for the next game. Stand-ins for Rob.
  vote: {
    ask: 'What next?',
    picked: (count: number, need: number) => `${count} of ${need}`,
    starting: (name: string) => `${name} in`,
    // The host phone's plank: start the game it picked, or hand the pick
    // back to the room.
    start: (name: string) => `Start ${name}`,
    release: 'Back to the vote',
    go: 'Go!',
    unknown: 'Something new',
  },

  // Count Together's stage break: how this llama did, and the upgrade vote.
  // Stand-ins for Rob.
  stage: {
    count: (count: number) => `There were ${count}`,
    mine: (guess: number, error: number, left: number) =>
      error === 0
        ? `You said ${guess}. Exact! ${left} left`
        : `You said ${guess}. ${error} off, ${left} left`,
    none: 'No answer from you this time',
    ask: 'Pick an upgrade',
    picked: (count: number, need: number) => `${count} of ${need}`,
    chosen: (name: string) => `The party picked ${name}`,
  },

  // Fill: the jar knockout. Stand-ins for Rob.
  fill: {
    rulesTitle: 'How to fill',
    rules: [
      'Tap to drop one sheep into your jar.',
      'Hold the bar to shut your jar.',
      'Closest to the number stays. Over is out first.',
    ],
    round: (round: number) => `Round ${round}`,
    finalRound: (game: number) => `Final, game ${game}`,
    collect: 'Collect',
    ask: (target: number) => `Drop sheep. Stop at ${target}.`,
    drop: 'Tap to drop',
    hold: 'Hold to lock',
    locked: 'Locked',
    closed: 'Lids on. Watch the screen.',
    haunt: 'Tap a jar to drop a ghost in it.',
    ghost: 'ghost',
    ghostNote: 'Ghosts do not count. They only get in the way.',
    youDropped: 'You dropped',
    exact: 'Right on it. You stay.',
    stays: (total: number, target: number) =>
      total < target ? `${target - total} short. You stay.` : `${total - target} over. You stay.`,
    out: (total: number, target: number) =>
      total > target
        ? `${total - target} over. You are out.`
        : `${target - total} short. You are out.`,
    tookGame: 'You take this game.',
    placed: 'You placed',
    points: (points: number) => `${points} for the night`,
    watch: 'Watch the screen.',
  },

  // Stand-ins for Rob, added with the tabs, the mood vote, emotes and tips.
  // "Employer satisfaction" is Garrett's.
  tabs: { count: 'Count', mood: 'Mood', board: 'Tips' },
  // The identity row's status line, by screen.
  status: {
    queue: 'In line for a seat',
    waiting: 'Waiting for the next round',
    counting: (stage: number) => `Round ${stage}`,
    pad: 'The count is over',
    locked: 'Locked in',
    result: 'The result',
    hold: 'Between rounds',
    run_over: 'Run over',
    vote: 'Picking the next game',
    stage: 'Between rounds',
  },
  lockedIn: 'Locked in',
  resultYou: 'You said',
  resultActual: 'There were',
  emotes: { heart: 'Heart', spit: 'Spit', what: 'Huh?', ping: 'Find me', zzz: 'Zzz' },
  satisfaction: 'Employer satisfaction',
  moodNone: 'No ratings yet',
  moodVotes: (votes: number) => (votes === 1 ? '1 llama rated' : `${votes} llamas rated`),
  moodAsk: 'How is work going?',
  moodNext: (seconds: number) =>
    `Rate again in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`,
  moodQueued: 'You can rate once you have a seat.',
  moods: { sad: 'Bad', meh: 'Fine', happy: 'Good' },
  boardEmpty: 'No tips yet.',
  boardYou: 'You',
  boardStats: (stages: number, streak: number) => `${stages} counted, ${streak} in a row`,
  boardDemerits: (demerits: number) => (demerits === 1 ? '1 demerit' : `${demerits} demerits`),
  tipped: (share: number) => `+${share} in tips`,
  demerit: (demerit: number) => `-${demerit} demerit`,
  tipMissed: 'Your count was too far off for a share.',
} as const;
